import React, { useState } from 'react';
import {
  HardHat, Plus, Search, AlertCircle, TrendingUp, Users,
  ChevronRight, Calendar, Clock, CheckCircle2,
  Edit3, Save, Trash2, MapPin, FileText,
  Image as ImageIcon, UploadCloud, Bot, Send, Loader2
} from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { obrasApi, obraTasksApi, obraMediaApi } from '@/core/adapter-api';
import { askProjectContextQuestion, getGroqKey } from '@/services/groqService';
import { Modal, FormField, StatsGrid, useToast, EmptyState, GanttChart, DetailPanel } from './shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ObraProject {
  id: string;
  name: string;
  shortName: string;
  status: 'terminada' | 'en_obra' | 'presupuestando';
  budget: number;
  progress: number;
  remaining: number;
  team: { initials: string; color: string; name: string }[];
  pendingTasks: number;
  progressColor: string;
  location: string;
  startDate: string;
  tasks: { id: string; name: string; status: 'done' | 'in_progress' | 'pending'; assignee: string; startDate?: string; endDate?: string }[];
}

// ─── Demo Data ────────────────────────────────────────────────────────────────






// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBudget(n: number): string {
  if (n >= 1000) return Math.round(n / 1000) + 'k€';
  return n + '€';
}

function StatusBadge({ status }: { status: 'terminada' | 'en_obra' | 'presupuestando' }) {
  if (status === 'terminada') {
    return (
      <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
        Terminada
      </span>
    );
  }
  if (status === 'presupuestando') {
    return (
      <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
        Presupuestando
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
      En Obra
    </span>
  );
}

function TaskStatusIcon({ status }: { status: 'done' | 'in_progress' | 'pending' }) {
  if (status === 'done') return <CheckCircle2 size={14} className="text-emerald-400" />;
  if (status === 'in_progress') return <Clock size={14} className="text-amber-400 animate-pulse" />;
  return <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-600" />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ObrasPage: React.FC = () => {
  const rawObras = useQuery(() => obrasApi.all(), [], ['obras']) || [];
  const rawTasks = useQuery(() => obraTasksApi.byObra(''), [], ['obra_tasks']) || []; 
  // We use a custom fetch for all tasks to map them below:
  const [allTasks, setAllTasks] = useState<any[]>([]);

  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const rawMedia = useQuery(async () => {
    if (!selectedProject) return [];
    return obraMediaApi.byObra(selectedProject);
  }, [selectedProject], ['obra_media']) || [];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !selectedProject) return;

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const dataUrl = ev.target?.result as string;
            if (dataUrl) {
                await obraMediaApi.add({
                    obraId: selectedProject,
                    url: dataUrl,
                    caption: file.name,
                    timestamp: Date.now(),
                    authorId: 'user'
                } as any);
            }
        };
        reader.readAsDataURL(file);
    }
    // reset input
    e.target.value = '';
  };

  React.useEffect(() => {
    // Instead of querying per obra, get all tasks via the raw adapter so useQuery reactive pattern is simpler
    // Wait, the easiest way to be reactive is useQuery(() => getAdapter().query('obra_tasks'))
    // Since we don't have getAdapter directly imported here, let's just make obraTasksApi.byObra return all if no id is passed
  }, []);

  // Alternative: query all tasks reactively
  const allTasksQuery = useQuery(async () => {
    // We can just fetch all tasks
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('obra_tasks');
  }, [], ['obra_tasks']) || [];

  const projects: ObraProject[] = rawObras.map(obra => {
    const pTasks = allTasksQuery.filter(t => t.obraId === obra.id).sort((a: any, b: any) => a.order - b.order);
    const doneTasks = pTasks.filter(t => t.status === 'done').length;
    const progress = pTasks.length > 0 ? Math.round((doneTasks / pTasks.length) * 100) : 0;
    
    return {
      id: obra.id,
      name: obra.name,
      shortName: obra.name.length > 35 ? obra.name.slice(0, 35) + '...' : obra.name,
      status: obra.status as 'terminada'|'en_obra'|'presupuestando',
      budget: obra.budget,
      progress: progress,
      remaining: 100 - progress,
      team: [], // Teams will be wired in Phase 5
      pendingTasks: pTasks.filter(t => t.status !== 'done').length,
      progressColor: progress === 100 ? 'bg-emerald-500' : 'bg-amber-500', // Basic color logic
      location: obra.location,
      startDate: obra.startDate,
      tasks: pTasks.map(t => ({
         id: t.id,
         name: t.name,
         status: t.status,
         assignee: t.assigneeId || 'Sin asignar',
         startDate: t.startDate,
         endDate: t.endDate
      })) as any
    };
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newObra, setNewObra] = useState({ name: '', location: '', budget: '', startDate: '', status: 'presupuestando' as 'presupuestando' | 'en_obra' });
  const [newTaskName, setNewTaskName] = useState('');
  const [taskView, setTaskView] = useState<'list' | 'gantt'>('list');
  const [showChat, setShowChat] = useState<{ isOpen: boolean; projectId: string }>({ isOpen: false, projectId: '' });
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant' | 'system', content: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatScrollRef = React.useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  const selected = projects.find(p => p.id === selectedProject);

  const totalBudget = projects.reduce((sum, p) => sum + p.budget, 0);
  const totalPendingTasks = projects.reduce((sum, p) => sum + p.pendingTasks, 0);
  const activeProjects = projects.filter(p => p.status === 'en_obra').length;

  const filteredProjects = searchQuery
    ? projects.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : projects;

  const addNewObra = async () => {
    if (!newObra.name.trim()) return;
    await obrasApi.add({
      name: newObra.name,
      location: newObra.location,
      budget: parseInt(newObra.budget) || 0,
      startDate: newObra.startDate || new Date().toISOString().split('T')[0],
      status: newObra.status as any,
      progress: 0,
      progressColor: 'bg-amber-500' // Base default
    });
    setNewObra({ name: '', location: '', budget: '', startDate: '', status: 'presupuestando' });
    setShowAddModal(false);
  };

  const deleteProject = async (id: string) => {
    if (!confirm('¿Eliminar esta obra? Esta acción es irreversible.')) return;
    await obrasApi.delete(id);
    if (selectedProject === id) setSelectedProject(null);
  };

  const toggleTaskStatus = async (projectId: string, taskIndex: number) => {
    const rawObraTasks = allTasksQuery.filter(t => t.obraId === projectId).sort((a: any, b: any) => a.order - b.order);
    const targetTask = rawObraTasks[taskIndex];
    if (!targetTask) return;

    const current = targetTask.status;
    const nextStatus = current === 'pending' ? 'in_progress' : current === 'in_progress' ? 'done' : 'pending';
    
    await obraTasksApi.update(targetTask.id, { status: nextStatus });
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() || !selected) return;
    const msg = chatInput.trim();
    setChatInput('');
    const newHistory = [...chatMessages, { role: 'user' as const, content: msg }];
    setChatMessages(newHistory);
    setChatLoading(true);

    try {
      const response = await askProjectContextQuestion(
        {
          projectName: selected.name,
          budget: selected.budget,
          tasksInfo: selected.tasks.map(t => `- ${t.name} (${t.status})`).join('\n')
        },
        msg,
        newHistory.map(m => ({ role: m.role, content: m.content }))
      );
      setChatMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setChatLoading(false);
    }
  };

  React.useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  return (
    <div className="flex gap-6 -mx-4 md:-mx-8 -my-8 h-[calc(100vh-0px)] md:h-screen">
      {/* Projects Sidebar - Hidden on mobile, shown on md+ */}
      <div className="hidden md:flex w-72 bg-[#0d1117] border-r border-white/8 flex-shrink-0 flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Proyectos</span>
          <button
            className="p-1 text-gray-500 hover:text-amber-400 transition-colors rounded"
            onClick={() => setShowAddModal(true)}
            title="Nueva obra"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-white/5">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar obra..."
              className="w-full bg-[#010409] border border-white/8 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-300 placeholder:text-gray-600 focus:border-amber-500/30 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredProjects.map((project) => (
            <button
              key={project.id}
              onClick={() => setSelectedProject(project.id === selectedProject ? null : project.id)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                selectedProject === project.id ? 'bg-amber-500/10 border-l-2 border-l-amber-500' : ''
              }`}
            >
              <p className="text-sm text-gray-200 font-medium truncate">{project.shortName}</p>
              <div className="flex items-center justify-between mt-1">
                <StatusBadge status={project.status} />
                <span className="text-[10px] text-gray-500">{formatBudget(project.budget)}</span>
              </div>
              <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${project.progressColor}`}
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </button>
          ))}
          {filteredProjects.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-600 text-xs">
              No se encontraron obras
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto py-8 pr-4 md:pr-8">
        {/* Mobile project selector - visible only on mobile */}
        <div className="md:hidden mb-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {projects.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedProject(p.id === selectedProject ? null : p.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  selectedProject === p.id
                    ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
                    : 'bg-[#0d1117] border-white/8 text-gray-400 hover:border-white/15'
                }`}
              >
                {p.name.slice(0, 20)}{p.name.length > 20 ? '...' : ''}
              </button>
            ))}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold border border-dashed border-white/10 text-gray-500 hover:text-amber-400 hover:border-amber-500/30 transition-colors"
            >
              + Nueva
            </button>
          </div>
        </div>

        {/* Header Stats */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Obras en curso</h1>
            <p className="text-sm text-gray-500 mt-1">
              {activeProjects} obras activas · {projects.filter(p => p.status === 'presupuestando').length} presupuestando
            </p>
          </div>
          <div className="flex items-center gap-8 text-right">
            <div>
              <p className="text-2xl font-bold text-gray-100">{projects.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Obras</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{formatBudget(totalBudget)}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Importe Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{totalPendingTasks}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tareas Pend.</p>
            </div>
          </div>
        </div>

        {/* Selected Project Detail or Project Cards */}
        {selected ? (
          <div className="space-y-6">
            {/* Detail Header */}
            <div className="bg-[#0d1117] border border-white/8 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-100">{selected.name}</h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                    <span className="flex items-center gap-1.5"><MapPin size={14} className="text-amber-500" />{selected.location}</span>
                    <span className="flex items-center gap-1.5"><Calendar size={14} className="text-amber-500" />{selected.startDate}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={selected.status} />
                  <button
                    onClick={async () => {
                      const raw = rawObras.find(o => o.id === selected.id);
                      if (!raw) return;
                      const next = raw.status === 'presupuestando' ? 'en_obra' : raw.status === 'en_obra' ? 'terminada' : 'presupuestando';
                      await obrasApi.update(selected.id, { status: next } as any);
                      addToast(`Estado cambiado a ${next === 'en_obra' ? 'En Obra' : next === 'terminada' ? 'Terminada' : 'Presupuestando'}`, 'success');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-xs font-medium transition-colors"
                    title="Cambiar estado"
                  >
                    <ChevronRight size={12} /> Avanzar
                  </button>
                  {getGroqKey() && (
                    <button
                      onClick={() => setShowChat({ isOpen: true, projectId: selected.id })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 rounded-lg text-xs font-semibold transition-colors"
                      title="Chat inteligente"
                    >
                      <Bot size={14} /> Asesor IA
                    </button>
                  )}
                  <button
                    onClick={() => deleteProject(selected.id)}
                    className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                    title="Eliminar obra"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-[#010409] rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Presupuesto</p>
                  <p className="text-xl font-bold text-amber-400 mt-1">{formatBudget(selected.budget)}</p>
                </div>
                <div className="bg-[#010409] rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Avance</p>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{selected.progress}%</p>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-2">
                    <div className={`h-full rounded-full ${selected.progressColor}`} style={{ width: `${selected.progress}%` }} />
                  </div>
                </div>
                <div className="bg-[#010409] rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tareas pendientes</p>
                  <p className="text-xl font-bold text-gray-100 mt-1">{selected.pendingTasks}</p>
                </div>
              </div>
            </div>

            {/* Team */}
            <div className="bg-[#0d1117] border border-white/8 rounded-xl p-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Equipo asignado</h3>
              {selected.team.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {selected.team.map((member, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#010409] rounded-lg px-3 py-2">
                      <div className={`w-7 h-7 rounded-full ${member.color} flex items-center justify-center text-[9px] font-bold text-white`}>
                        {member.initials}
                      </div>
                      <span className="text-sm text-gray-300">{member.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Sin equipo asignado todavía</p>
              )}
            </div>

            {/* Tasks */}
            <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden mt-6">
              <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Tareas del proyecto</h3>
                  <span className="text-[10px] text-gray-600">{selected.tasks.length} tareas</span>
                </div>
                <div className="flex gap-1 p-1 bg-white/5 rounded-lg border border-white/10">
                  <button onClick={() => setTaskView('list')} className={`px-3 py-1 text-xs rounded-md transition-all ${taskView === 'list' ? 'bg-amber-500/20 text-amber-400 shadow-sm border border-amber-500/30' : 'text-gray-500 hover:text-gray-300'}`}>Lista</button>
                  <button onClick={() => setTaskView('gantt')} className={`px-3 py-1 text-xs rounded-md transition-all ${taskView === 'gantt' ? 'bg-amber-500/20 text-amber-400 shadow-sm border border-amber-500/30' : 'text-gray-500 hover:text-gray-300'}`}>Gantt</button>
                </div>
              </div>
              
              {/* Inline task add */}
              <div className="px-5 py-3 border-b border-white/5 bg-[#080c12]">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newTaskName.trim()) return;
                    const order = allTasksQuery.filter(t => t.obraId === selected.id).length;
                    await obraTasksApi.add({
                      obraId: selected.id,
                      name: newTaskName.trim(),
                      status: 'pending',
                      order,
                      assigneeId: '',
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0],
                    } as any);
                    setNewTaskName('');
                    addToast('Tarea añadida correctamente', 'success');
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus size={14} className="text-gray-600 flex-shrink-0" />
                  <input
                    value={newTaskName}
                    onChange={e => setNewTaskName(e.target.value)}
                    placeholder="Añadir nueva tarea..."
                    className="flex-1 bg-transparent border-none text-sm text-gray-300 placeholder:text-gray-600 focus:outline-none"
                  />
                  {newTaskName.trim() && (
                    <button type="submit" className="px-3 py-1 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-500 transition-colors">
                      Añadir
                    </button>
                  )}
                </form>
              </div>

              {taskView === 'list' ? (
                selected.tasks.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {selected.tasks.map((task, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between px-5 py-3 hover:bg-white/3 transition-colors cursor-pointer group"
                        onClick={() => toggleTaskStatus(selected.id, i)}
                        title="Clic para cambiar estado"
                      >
                        <div className="flex items-center gap-3">
                          <TaskStatusIcon status={task.status} />
                          <span className={`text-sm ${task.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                            {task.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">{task.assignee}</span>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const rawObraTasks = allTasksQuery.filter(t => t.obraId === selected.id).sort((a: any, b: any) => a.order - b.order);
                              const target = rawObraTasks[i];
                              if (target) {
                                await obraTasksApi.delete(target.id);
                                addToast('Tarea eliminada', 'info');
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-400 transition-all"
                            title="Eliminar tarea"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-gray-600 text-sm">
                    Sin tareas definidas. Usa el campo de arriba para añadir la primera.
                  </div>
                )
              ) : (
                <GanttChart tasks={selected.tasks as any} />
              )}
            </div>

            {/* Photo Gallery */}
            <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden mt-6">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                  <ImageIcon size={14} /> Galería fotográfica
                </h3>
                <label className="cursor-pointer flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-xs font-medium transition-colors">
                  <UploadCloud size={14} /> Añadir
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handleFileUpload} />
                </label>
              </div>
              <div className="p-5">
                {rawMedia.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {rawMedia.map((media: any) => (
                      <div key={media.id} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#010409]">
                        <img src={media.url} alt={media.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                           <div className="flex justify-end">
                             <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); obraMediaApi.delete(media.id); }} className="p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded cursor-pointer transition-colors" title="Eliminar foto">
                                <Trash2 size={12} />
                             </button>
                           </div>
                           <p className="text-[10px] text-gray-300 truncate" title={media.caption}>{media.caption}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 border-2 border-dashed border-white/5 rounded-xl">
                    <ImageIcon size={32} className="mx-auto text-gray-600 mb-3" />
                    <p className="text-sm text-gray-400 font-medium">No hay fotos en la galería.</p>
                    <p className="text-xs text-gray-600 mt-1">Sube imágenes para documentar el avance.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Project Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-[#0d1117] border border-white/8 rounded-xl p-5 hover:border-amber-500/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedProject(project.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-200 leading-tight line-clamp-2">
                        {project.name.length > 40 ? project.name.slice(0, 40) + '...' : project.name}
                      </h3>
                    </div>
                    <StatusBadge status={project.status} />
                  </div>

                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Presupuesto</p>
                  <p className="text-xl font-bold text-amber-400 mb-3">{formatBudget(project.budget)}</p>

                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                    <span>{project.progress}% completado</span>
                    <span>{project.remaining}% restante</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-full ${project.progressColor}`}
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {project.team.map((member, i) => (
                        <div
                          key={i}
                          className={`w-7 h-7 rounded-full ${member.color} flex items-center justify-center text-[9px] font-bold text-white border-2 border-[#0d1117]`}
                          title={member.name}
                        >
                          {member.initials}
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{project.pendingTasks} tareas pend.</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Urgent Tasks */}
              <div className="bg-[#0d1117] border border-white/8 rounded-xl p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
                  Tareas Urgentes
                </h3>
                <div className="space-y-3">
                  {(() => {
                    const urgentTasks = allTasksQuery
                      .filter(t => t.status !== 'done')
                      .slice(0, 5)
                      .map(t => {
                        const obra = rawObras.find(o => o.id === t.obraId);
                        const prio = t.status === 'in_progress' ? 'alta' : 'media';
                        const prioColor = prio === 'alta' ? 'text-red-400' : 'text-amber-400';
                        return { name: t.name, priority: prio, obra: obra?.name?.slice(0, 15) || '?', color: prioColor };
                      });
                    return urgentTasks.length > 0 ? urgentTasks.map((task, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${task.color.replace('text-', 'bg-')}`} />
                          <span className="text-sm text-gray-300">{task.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${task.color}`}>{task.priority}</span>
                          <span className="text-xs text-gray-500 font-medium">{task.obra}</span>
                        </div>
                      </div>
                    )) : (
                      <p className="text-sm text-gray-600 text-center py-4">🎉 Sin tareas urgentes</p>
                    );
                  })()}
                </div>
              </div>

              {/* Costs by Project */}
              <div className="bg-[#0d1117] border border-white/8 rounded-xl p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
                  Costes por Obra
                </h3>
                <div className="space-y-4">
                  {projects.map((project) => {
                    const costPercent = Math.round((project.progress / 100) * 100);
                    return (
                      <div key={project.id}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-gray-300 truncate">{project.shortName}</span>
                          <span className="text-sm font-bold text-gray-200">{formatBudget(Math.round(project.budget * project.progress / 100))}</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${project.progressColor}`}
                            style={{ width: `${costPercent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Obra Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Nueva Obra">
        <div className="space-y-4">
          <FormField
            label="Nombre del proyecto"
            value={newObra.name}
            onChange={(v) => setNewObra({ ...newObra, name: v })}
            placeholder="Ej: Construcción Chalet Av. Gran Vía 15"
          />
          <FormField
            label="Ubicación"
            value={newObra.location}
            onChange={(v) => setNewObra({ ...newObra, location: v })}
            placeholder="Ej: Av. Gran Vía 15, Zaragoza"
          />
          <FormField
            label="Presupuesto (€)"
            value={newObra.budget}
            onChange={(v) => setNewObra({ ...newObra, budget: v })}
            type="number"
            placeholder="Ej: 150000"
          />
          <FormField
            label="Fecha de inicio"
            value={newObra.startDate}
            onChange={(v) => setNewObra({ ...newObra, startDate: v })}
            type="date"
          />
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Estado inicial</label>
            <select
              value={newObra.status}
              onChange={(e) => setNewObra({ ...newObra, status: e.target.value as 'en_obra' | 'presupuestando' })}
              className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-amber-500/50 focus:outline-none"
            >
              <option value="presupuestando">Presupuestando</option>
              <option value="en_obra">En Obra</option>
            </select>
          </div>
          <button
            onClick={addNewObra}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors mt-2"
          >
            <Plus size={16} /> Crear obra
          </button>
        </div>
      </Modal>

      {/* AI Chat Detail Panel */}
      <DetailPanel 
        title={
          <div className="flex items-center gap-2">
            <Bot size={18} className="text-purple-400" />
            <span className="text-white">Asistente de Obra</span>
          </div>
        }
        open={showChat.isOpen} 
        onClose={() => setShowChat({ isOpen: false, projectId: '' })}
      >
        <div className="flex flex-col h-full bg-[#0d1117] rounded-xl border border-white/8 overflow-hidden">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={chatScrollRef}>
            {chatMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  <Bot size={32} className="text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-200 mb-2">¡Hola! Soy tu IA experta.</h3>
                <p className="text-sm text-gray-500 max-w-xs">
                  Pregúntame sobre el presupuesto, las tareas pendientes o recomendaciones para "{selected?.name}". Llama 3.3 está a tu servicio.
                </p>
              </div>
            ) : (
              chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-amber-600 text-white rounded-br-none' 
                      : 'bg-[#161b22] border border-white/10 text-gray-300 rounded-bl-none'
                  }`}>
                    {msg.role === 'assistant' && <Bot size={12} className="text-purple-400 mb-1" />}
                    <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  </div>
                </div>
              ))
            )}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-[#161b22] border border-white/10 text-gray-500 rounded-2xl rounded-bl-none px-4 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-purple-400" /> Pensando...
                </div>
              </div>
            )}
          </div>

          {/* Chat Input */}
          <div className="p-3 bg-[#010409] border-t border-white/8">
            <form 
              onSubmit={e => { e.preventDefault(); handleSendChatMessage(); }}
              className="relative flex items-center"
            >
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Pregunta algo sobre el proyecto..."
                disabled={chatLoading}
                className="w-full bg-[#0d1117] border border-white/10 rounded-xl pl-4 pr-12 py-3 text-sm text-gray-200 placeholder:text-gray-600 focus:border-amber-500/30 focus:outline-none disabled:opacity-50"
              />
              <button 
                type="submit" 
                disabled={!chatInput.trim() || chatLoading}
                className="absolute right-2 p-2 bg-amber-500 text-amber-950 rounded-lg disabled:opacity-50 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-amber-400 transition-colors"
              >
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>
      </DetailPanel>
    </div>
  );
};

export default ObrasPage;
